import type { Theme } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import {
  $blackColor,
  $whiteColor,
  $darkDisabledFont,
  $darkPanelsFont,
  $diastoleColor,
  $lightPanelsFont,
  $lightThemeMainBgColor,
  $lightThemeSecondaryBgColor,
  $selectedTableRowColor,
  $systoleColor,
  $selectedObjectColor,
  $disabledObjectColor,
  $labelColor,
  $labelBgColor,
  $demiBlackColor,
  $highlightedTableRowColor,
  $disabledDarkPanelFontColor,
  $disabledLightPanelFontColor,
  $disabledLightPanelFontColorHighlighted,
  $disabledDarkPanelFontColorHighlighted,
  $labelSelectedBgColor,
  $labelHoveredBgColor,
  $wheatColor,
  $lightCollapsibleBoxHeaderBgColor,
  $lightCollapsibleBoxBgColor,
  $darkCollapsibleBoxBgColor,
  $darkCollapsibleBoxHeaderBgColor
} from '@/assets/branding/branding.constants.ts';

export const lightCustomPalette = {
  panels: {
    side: {
      background: $lightThemeMainBgColor,
      font: $lightPanelsFont
    },
    header: {
      background: $lightThemeSecondaryBgColor,
      font: $lightPanelsFont
    },
    stepper: {
      background: $lightThemeSecondaryBgColor,
      font: $lightPanelsFont
    },
    predefinedViews: 'rgba(241, 241, 241, 0.5)',
    collapseImagesBar: {
      background: $lightThemeSecondaryBgColor,
      font: $lightPanelsFont
    },
    collapsibleBoxes: {
      header: {
        background: $lightCollapsibleBoxHeaderBgColor,
        font: $lightPanelsFont
      },
      body: {
        background: $lightCollapsibleBoxBgColor
      }
    },
    mainarea: $lightThemeMainBgColor,
    threeD: $lightThemeMainBgColor,
    images: $blackColor,
    arrowsOutline: '#909dac',
    imageContainer: $blackColor,
    tableRowHighlighted: $highlightedTableRowColor,
    tableRowSelected: $selectedTableRowColor,
    cineloop: $whiteColor,
    threeDButton: {
      font: '#202a34',
      background: 'rgba(71, 71, 71, 0.1)'
    }
  },
  fonts: { disabledFont: $disabledLightPanelFontColor, highlighted: $disabledLightPanelFontColorHighlighted },
  objects: {
    selected: $selectedObjectColor,
    apDistance: $systoleColor,
    slDistance: $diastoleColor,
    disabled: $disabledObjectColor
  },
  labels: {
    color: $labelColor,
    background: $labelBgColor,
    selectedBackground: $labelSelectedBgColor,
    hoveredBackground: $labelHoveredBgColor,
    border: '#111111'
  },
  colors: {
    white: $whiteColor,
    black: $blackColor,
    demiBlack: $demiBlackColor,
    wheat: $wheatColor
  }
};

export type CustomPaletteType = typeof lightCustomPalette;

export const darkCustomPalette: CustomPaletteType = {
  ...lightCustomPalette,
  panels: {
    ...lightCustomPalette.panels,
    side: {
      background: 'rgba(32, 40, 47, 1)',
      font: $darkPanelsFont
    },
    header: {
      background: 'rgba(32, 40, 47, 0.9)',
      font: $darkPanelsFont
    },
    stepper: {
      background: 'rgba(32, 40, 47, 0.9)',
      font: $darkPanelsFont
    },
    predefinedViews: 'rgba(0, 0, 0, 0.5)',
    collapseImagesBar: {
      background: 'rgba(32, 40, 47, 0.9)',
      font: $darkPanelsFont
    },
    collapsibleBoxes: {
      header: {
        background: $darkCollapsibleBoxHeaderBgColor,
        font: $darkPanelsFont
      },
      body: {
        background: $darkCollapsibleBoxBgColor
      }
    },
    mainarea: $blackColor,
    threeD: $blackColor,
    cineloop: '#3a4653',
    threeDButton: { font: $whiteColor, background: 'rgba(71, 71, 71, 0.3)' }
  },
  fonts: { disabledFont: $disabledDarkPanelFontColor, highlighted: $disabledDarkPanelFontColorHighlighted },
  objects: {
    ...lightCustomPalette.objects,
    apDistance: $systoleColor,
    slDistance: $diastoleColor
  },
  labels: {
    ...lightCustomPalette.labels,
    border: '#7f7f7f'
  }
};

const reportCustomPalette = {
  ...lightCustomPalette,
  panels: { ...lightCustomPalette.panels, threeD: $whiteColor }
};

const lightMuiComponents = {
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: lightCustomPalette.panels.tableRowSelected
        }
      }
    }
  }
};

const darkMuiComponents = {
  ...lightMuiComponents,
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: $blackColor,
        color: $whiteColor
      }
    }
  },
  MuiTableCell: {
    // to make row styles work
    styleOverrides: { root: { color: 'inherit' } }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        backgroundColor: 'inherit',
        '& > td, & > th': {
          borderBottom: `1px solid ${darkCustomPalette.panels.side.background}`
        },
        '&.Mui-selected > td': {
          backgroundColor: $selectedTableRowColor,
          color: $blackColor
        },
        '&.Mui-selected > th': {
          backgroundColor: $selectedTableRowColor,
          color: $blackColor
        },
        '&.Mui-selected input': {
          color: $blackColor
        }
      }
    }
  },
  // modal separators
  MuiDialogContent: {
    styleOverrides: {
      root: {
        borderTop: '1px solid rgba(255, 255, 255,  0.46)',
        borderBottom: '1px solid rgba(255, 255, 255,  0.46)',
        '& svg': {
          fill: $whiteColor
        }
      }
    }
  },
  // user preferences modal labels
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: $whiteColor,
        borderColor: $whiteColor
      }
    }
  },

  // sidebar input labels
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: $whiteColor,
        borderColor: $whiteColor,
        backgroundColor: 'rgba(32, 40, 47, 1)'
      }
    }
  },
  // sidebar slider labels
  MuiTypography: {
    styleOverrides: { root: { color: $whiteColor } }
  },
  // sidebar trash icon
  MuiButtonBase: {
    styleOverrides: { root: { color: 'inherit' } }
  },
  MuiButton: {
    //approved step back-next buttons
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: $darkDisabledFont,
          borderColor: $darkDisabledFont
        }
      }
    }
  },
  // sidebar dropdown (e.g. valve size)
  MuiMenuItem: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.5)'
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(0, 177, 234, 0.5)'
        },
        '&.Mui-selected:hover': {
          backgroundColor: 'rgba(0, 177, 234, 0.7)'
        }
      }
    }
  },
  //sidebar editing measurements input
  MuiInputBase: {
    styleOverrides: { root: { color: 'inherit' } }
  },
  // eye off and non-contour icons
  MuiCheckbox: {
    styleOverrides: { root: { color: 'inherit' } }
  }
};

const config = {
  light: {
    font: 'inherit',
    contrastFont: undefined,
    disabledFont: $disabledDarkPanelFontColor,
    customPalette: lightCustomPalette,
    secondary: {
      main: $lightPanelsFont,
      light: '#3a4653'
    },
    muiComponents: lightMuiComponents
  },
  dark: {
    font: $whiteColor,
    disabledFont: $darkDisabledFont,
    contrastFont: 'black',
    customPalette: darkCustomPalette,
    secondary: {
      main: $darkPanelsFont,
      light: $whiteColor
    },
    muiComponents: darkMuiComponents
  },
  report: {
    font: 'inherit',
    contrastFont: undefined,
    disabledFont: $disabledDarkPanelFontColor,
    customPalette: reportCustomPalette,
    secondary: {
      main: $lightPanelsFont,
      light: '#3a4653'
    },
    muiComponents: lightMuiComponents
  }
};

// A custom theme for this app
export const getTheme = (mode: 'light' | 'dark' | 'report'): Theme => {
  const theme = {
    mode,
    font: config[mode].font,
    contrastFont: config[mode].contrastFont,
    disabledFont: config[mode].disabledFont,
    palette: {
      primary: {
        main: '#00b1ea',
        light: '#11c5ff',
        dark: '#0058a5',
        contrastText: $whiteColor
      },
      secondary: {
        main: config[mode].secondary.main,
        light: config[mode].secondary.light,
        dark: $blackColor,
        contrastText: $whiteColor
      },
      success: {
        main: '#01c0b9',
        light: '#00dcd4',
        dark: '#00908b',
        contrastText: $whiteColor
      }
    },
    components: config[mode].muiComponents,
    typography: {
      fontFamily: '"Metronic Pro", "Roboto", "Helvetica", "Arial", sans-serif'
    },
    customPalette: config[mode].customPalette
  };
  return createTheme(theme);
};
